/**
 * Ce que la cuisine dit, et ce que l'application en comprend (US-60).
 *
 * Séparé du branchement au navigateur (`voiceCommands.ts`) parce que c'est de
 * l'analyse de texte pure : aucune API, aucun état, et donc entièrement
 * vérifiable sans micro — ce qui compte pour une fonction dont on ne peut pas
 * tester la partie navigateur en machine.
 *
 * ## Trois élargissements, et leur limite
 *
 * ### 1. On écoute les autres hypothèses du navigateur
 *
 * `SpeechRecognition` renvoie plusieurs transcriptions candidates par énoncé,
 * classées par confiance. L'ancien code ne lisait que la première. Or « suivant »
 * prononcé dans une cuisine bruyante ressort souvent en « suivons » ou
 * « survivant » en tête, avec le bon mot en deuxième position. On examine
 * désormais **toutes** les hypothèses fournies, dans l'ordre de confiance, et on
 * retient la première qui porte une commande.
 *
 * ### 2. On tolère un caractère d'écart
 *
 * Au-delà des hypothèses, la reconnaissance transcrit couramment « suivante »
 * en « suivent », « précédent » en « précédant », « minuteur » en « minuteure ».
 * Une distance d'édition de 1 rattrape ces cas.
 *
 * **Seulement sur les mots d'au moins six lettres** : à distance 1, « avant »
 * (précédent) attraperait « avent », « avans », mais aussi « avait » — et une
 * commande déclenchée à tort fait perdre l'étape en cours, ce qui est bien pire
 * que de ne pas déclencher. C'est la règle d'US-31, et elle ne bouge pas.
 *
 * ### 3. On entend les nombres, chiffres comme lettres
 *
 * « minuteur dix minutes » et « minuteur 10 » arment un minuteur de dix minutes
 * sans passer par la durée de l'étape ; « étape 3 » y va directement. Les deux
 * écritures arrivent réellement : le navigateur transcrit tantôt « 10 », tantôt
 * « dix », selon la phrase et la version.
 */

const COMBINING_DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

/** Longueur minimale pour tolérer une faute : en deçà, le risque de déclenchement à tort l'emporte. */
const LONGUEUR_MIN_TOLERANCE = 6;

export type VoiceCommandKind = 'next' | 'previous' | 'timer' | 'repeat' | 'ingredients' | 'step';

/**
 * Ce que l'application a compris. Un objet plutôt qu'une constante de chaîne :
 * « minuteur dix minutes » et « étape trois » portent une valeur, et la
 * transporter dans l'intention évite de ré-analyser la phrase plus loin.
 */
export type VoiceIntent =
  | { kind: 'next' }
  | { kind: 'previous' }
  | { kind: 'repeat' }
  | { kind: 'ingredients' }
  /** `minutes` : durée dictée, ou `null` pour « la durée de l'étape ». */
  | { kind: 'timer'; minutes: number | null }
  /** `numero` : rang parlé, à partir de 1. */
  | { kind: 'step'; numero: number };

/**
 * Mots reconnus. Volontairement peu nombreux et sans synonymes exotiques
 * (US-31) : le vocabulaire s'élargit ici de deux commandes réellement
 * demandées en cuisine — se faire relire l'étape, et voir les ingrédients —
 * mais pas d'un dictionnaire de synonymes.
 *
 * **Trois synonymes d'US-31 ont été retirés** : « avant », « après » et
 * « continue ». Ce sont des mots de liaison ordinaires — « avant de mélanger »,
 * « après avoir battu les blancs », « ça continue de cuire » —, et le micro
 * reste ouvert pendant qu'on parle à quelqu'un dans la cuisine. Chacun faisait
 * donc perdre l'étape en cours sur une phrase anodine, ce que la règle d'US-31
 * refuse explicitement. La tolérance d'un caractère et la lecture de toutes les
 * hypothèses du navigateur, ajoutées ici, couvrent largement ce qu'ils
 * apportaient.
 */
const MOTS: ReadonlyArray<[VoiceCommandKind, readonly string[]]> = [
  ['next', ['suivant', 'suivante']],
  ['previous', ['precedent', 'precedente', 'retour', 'reviens', 'recule']],
  ['timer', ['minuteur', 'minuterie', 'chrono', 'chronometre']],
  ['repeat', ['repete', 'repeter', 'relis', 'redis', 'repetition']],
  ['ingredients', ['ingredient', 'ingredients']],
  ['step', ['etape']],
];

const UNITES: Record<string, number> = {
  zero: 0, un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7,
  huit: 8, neuf: 9, dix: 10, onze: 11, douze: 12, treize: 13, quatorze: 14,
  quinze: 15, seize: 16, vingt: 20, trente: 30, quarante: 40, cinquante: 50,
  soixante: 60,
};

/** Retire accents, ponctuation et casse : « Précédent, » et « precedent » sont le même mot. */
export function motsEntendus(transcript: string): string[] {
  return transcript
    .normalize('NFD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((mot) => mot.length > 0);
}

/**
 * Distance d'édition, plafonnée à 1 : on n'a pas besoin de la valeur exacte,
 * seulement de savoir si un caractère suffit à passer d'un mot à l'autre. Sortie
 * anticipée dès que deux écarts sont trouvés.
 */
export function distanceUn(a: string, b: string): boolean {
  if (a === b) return true;
  const ecart = a.length - b.length;
  if (ecart > 1 || ecart < -1) return false;

  if (a.length === b.length) {
    let differences = 0;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        differences += 1;
        if (differences > 1) return false;
      }
    }
    return differences === 1;
  }

  // Une insertion ou une suppression : on avance en parallèle et on saute une
  // fois dans le plus long.
  const [court, long] = ecart < 0 ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let saute = false;
  while (i < court.length && j < long.length) {
    if (court[i] === long[j]) { i += 1; j += 1; continue; }
    if (saute) return false;
    saute = true;
    j += 1;
  }
  return true;
}

function commandeDuMot(mot: string): VoiceCommandKind | null {
  for (const [kind, alias] of MOTS) {
    if (alias.includes(mot)) return kind;
  }
  // Tolérance d'un caractère, réservée aux mots assez longs pour que
  // l'approximation ne devienne pas un déclenchement à tort.
  if (mot.length < LONGUEUR_MIN_TOLERANCE) return null;
  for (const [kind, alias] of MOTS) {
    for (const forme of alias) {
      if (forme.length >= LONGUEUR_MIN_TOLERANCE && distanceUn(mot, forme)) return kind;
    }
  }
  return null;
}

/**
 * Nombre lu à partir d'un mot, en chiffres ou en lettres. `null` si ce mot n'en
 * est pas un.
 */
function nombreDuMot(mot: string): number | null {
  if (/^\d+$/.test(mot)) {
    const valeur = Number.parseInt(mot, 10);
    return Number.isSafeInteger(valeur) ? valeur : null;
  }
  return UNITES[mot] ?? null;
}

/**
 * Nombre dicté à partir de la position `depart`, composition comprise :
 * « vingt cinq », « trente-cinq » (déjà découpé en deux mots), « quarante et
 * cinq ». Renvoie `null` si aucun nombre ne commence là.
 */
function nombreDicte(mots: string[], depart: number): number | null {
  const premier = depart < mots.length ? nombreDuMot(mots[depart] ?? '') : null;
  if (premier === null) return null;

  // Composition des dizaines : « vingt » suivi d'une unité. Un « et » de
  // liaison peut s'intercaler (« quarante et un »).
  if (premier >= 20 && premier % 10 === 0) {
    const suivant = mots[depart + 1] === 'et' ? depart + 2 : depart + 1;
    const unite = suivant < mots.length ? nombreDuMot(mots[suivant] ?? '') : null;
    if (unite !== null && unite < 10) return premier + unite;
  }
  return premier;
}

/**
 * Intention reconnue dans une phrase entendue, ou `null`.
 *
 * **Le dernier mot de commande prononcé l'emporte** (règle d'US-31) : on se
 * reprend en parlant — « suivant, non, minuteur » — et c'est la dernière
 * intention qui compte. Le nombre, lui, se lit après le mot de commande :
 * « minuteur dix minutes », jamais « dix minuteur ».
 */
export function intentFromTranscript(transcript: string): VoiceIntent | null {
  const mots = motsEntendus(transcript);
  for (let i = mots.length - 1; i >= 0; i -= 1) {
    const kind = commandeDuMot(mots[i] ?? '');
    if (kind === null) continue;

    if (kind === 'timer') {
      const minutes = nombreDicte(mots, i + 1);
      // Une durée nulle ou absurde vaut « pas de durée dictée » : mieux vaut
      // retomber sur celle de l'étape que d'armer un minuteur de zéro minute.
      return { kind: 'timer', minutes: minutes !== null && minutes > 0 && minutes <= 600 ? minutes : null };
    }

    if (kind === 'step') {
      const numero = nombreDicte(mots, i + 1);
      // « étape » sans numéro ne désigne rien : on n'invente pas de destination.
      if (numero === null || numero < 1) return null;
      return { kind: 'step', numero };
    }

    return { kind };
  }
  return null;
}

/**
 * Première intention trouvée parmi les hypothèses du navigateur, classées par
 * confiance décroissante. Le mot juste est souvent en deuxième position quand
 * la cuisine est bruyante.
 */
export function intentFromAlternatives(transcripts: readonly string[]): VoiceIntent | null {
  for (const transcript of transcripts) {
    const intent = intentFromTranscript(transcript);
    if (intent !== null) return intent;
  }
  return null;
}
